/**
 * Generates a random color in hex format (#RRGGBB) that ensures good contrast with white or black text.
 * @param preferWhiteText - If true, ensures the color works well with white text. If false, ensures it works well with black text.
 * @returns A random color string in hex format (e.g., "#a1b2c3")
 */
export function generateRandomColor(preferWhiteText: boolean = true): string {
  function getLuminance(r: number, g: number, b: number): number {
    const [R, G, B] = [r, g, b].map(channel => {
      const c = channel / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }

  function getContrastRatio(luminance1: number, luminance2: number): number {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  for (let attempts = 0; attempts < 100; attempts++) {
    // Generate random values for red, green, and blue components
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);

    // Calculate luminance and contrast ratio
    const backgroundLuminance = getLuminance(r, g, b);
    const whiteLuminance = getLuminance(255, 255, 255);
    const blackLuminance = getLuminance(0, 0, 0);

    const contrastWithWhite = getContrastRatio(backgroundLuminance, whiteLuminance);
    const contrastWithBlack = getContrastRatio(backgroundLuminance, blackLuminance);

    // Check contrast based on preference
    if (preferWhiteText && contrastWithWhite >= 4.5) {
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    } else if (!preferWhiteText && contrastWithBlack >= 4.5) {
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
  }

  // Fallback to black if no suitable color is found
  return preferWhiteText ? '#000000' : '#FFFFFF';
}
